import { request } from "../../api";
import { unwrapList } from "../devices";
import type { Platform } from "../devices";

/**
 * Admin-facing client for `AppGroup` resources — the app-equivalent of
 * `URLGroup`. Each group is a named bundle of administrator-authored
 * package or bundle identifiers. The compiler emits those identifiers into a
 * policy's `allowed_apps[*]`; they never depend on device inventory.
 *
 * Mirrors the URL groups module on purpose: list/create/patch/delete +
 * nested members. See `docs/backend/api-admin.md` for the contract and
 * `docs/changes/2026-05-25-policy-allowed-apps.md` for the wire shape.
 */
export interface AppGroupRow {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  members_count: number;
  rule_usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface AppGroupMemberRow {
  id: string;
  package_id: string;
  display_name: string;
  platform: Platform;
  created_at: string;
}

export interface CuratedAppGroupMemberInput {
  package_id: string;
  display_name?: string;
  platform: Platform;
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export const appGroupsApi = {
  list: async () => {
    const data = await request<Paginated<AppGroupRow> | AppGroupRow[]>(
      `/api/admin/v1/app-groups/`,
    );
    return unwrapList(data);
  },
  create: (input: { name: string; description?: string; is_active?: boolean }) =>
    request<AppGroupRow>(`/api/admin/v1/app-groups/`, { method: "POST", json: input }),
  patch: (
    id: string,
    body: Partial<{ name: string; description: string; is_active: boolean }>,
  ) =>
    request<AppGroupRow>(`/api/admin/v1/app-groups/${id}/`, {
      method: "PATCH",
      json: body,
    }),
  delete: (id: string) =>
    request<undefined>(`/api/admin/v1/app-groups/${id}/`, { method: "DELETE" }),
  listMembers: (id: string) =>
    request<AppGroupMemberRow[]>(`/api/admin/v1/app-groups/${id}/members/`),
  addMember: (id: string, body: CuratedAppGroupMemberInput) =>
    request<AppGroupMemberRow>(`/api/admin/v1/app-groups/${id}/members/`, {
      method: "POST",
      json: body,
    }),
  removeMember: (id: string, memberId: string) =>
    request<undefined>(
      `/api/admin/v1/app-groups/${id}/members/${memberId}/`,
      { method: "DELETE" },
    ),
};
